import * as React from 'react';

import { cn } from '@/ux-common/lib/utils';

/**
 * Icon - Minimal React wrapper for the decorated icon chrome.
 *
 * All visual styling is applied via CSS classes. The wrapper provides:
 * - `data-component="Icon"` for component identification
 * - Ref forwarding for tooltip/popover compatibility
 * - `className` merging via `cn()`
 *
 * The `.icon` base class is applied automatically (defaults: transparent variant,
 * sm size = 24px, round shape). Compose modifiers via className:
 * `.icon-{variant}`, `.icon-{sm|md|lg}`, `.icon-{round|square}`, `.icon-{overlap|pulse}`.
 *
 * The `children` slot is the glyph — a Lucide SVG, inline SVG, or `<img>` with
 * `className="glyph glyph-{sm|md|lg}"` applied directly. Pair `icon-sm` with
 * `glyph-sm` (16px inside 24px chrome), `icon-md` with `glyph-md` (20px inside 30px
 * chrome) and `icon-lg` with `glyph-lg` (24px inside 36px chrome).
 *
 * Accessibility: pass `aria-label` (or `aria-labelledby`) when the icon carries
 * meaning, and the wrapper sets `role="img"` for you — ARIA prohibits those
 * attributes on a bare `<span>`, so without the role the name is dropped. Leave
 * both off for decorative icons; the span stays generic and out of the a11y
 * tree, with the glyph marked `aria-hidden="true"`.
 *
 * @example
 * ```tsx
 * // Default Icon (transparent + sm + round are baked into .icon)
 * <Icon><AlertCircle className="glyph glyph-sm" aria-hidden="true" /></Icon>
 *
 * // Critical variant with pulse
 * <Icon className="icon-critical icon-pulse" aria-label="Active alarm">
 *   <AlertCircle className="glyph glyph-sm" aria-hidden="true" />
 * </Icon>
 *
 * // Brand square, large
 * <Icon className="icon-brand icon-square icon-lg">
 *   <Sparkle className="glyph glyph-lg" aria-hidden="true" />
 * </Icon>
 *
 * // Tooltip composition (single ref-forwarded span)
 * const IconWithTooltip = withTooltip(Icon);
 * <IconWithTooltip className="icon-warning" tooltipContent="Threshold exceeded">
 *   <Triangle className="glyph glyph-sm" aria-hidden="true" />
 * </IconWithTooltip>
 * ```
 */

export type IconProps = React.HTMLAttributes<HTMLSpanElement>;

export const Icon = React.forwardRef<HTMLSpanElement, IconProps>(
  ({ className, children, role, ...props }, ref) => {
    /* A bare <span> carries the generic role, and ARIA prohibits aria-label and
       aria-labelledby on generic — assistive tech drops the name silently, and
       axe flags it as aria-prohibited-attr (serious, WCAG 4.1.2).

       So when the caller supplies a name, default the role to `img`, which is
       what a labelled icon actually is. Decorative icons pass no name and stay
       generic, keeping them out of the accessibility tree. An explicit `role`
       always wins. */
    const isLabelled = props['aria-label'] != null || props['aria-labelledby'] != null;
    const resolvedRole = role ?? (isLabelled ? 'img' : undefined);

    return (
      <span
        ref={ref}
        data-component="Icon"
        role={resolvedRole}
        className={cn('icon', className)}
        {...props}
      >
        {children}
      </span>
    );
  }
);

Icon.displayName = 'Icon';
